import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey, CheckCircle, CircleNotch, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [tokenValid, setTokenValid] = useState(null);   // null=checking, true=ok, false=invalid
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setTokenValid(false); return; }
    api.get(`/auth/reset-check/${encodeURIComponent(token)}`)
      .then(r => setTokenValid(!!r.data.valid))
      .catch(() => setTokenValid(false));
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Reset failed."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center">
            <LockKey size={17} className="text-slate-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-[#0F1E38]">Set New Password</div>
            <div className="text-[11px] text-slate-500">Choose a strong password</div>
          </div>
        </div>

        {tokenValid === null && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
            <CircleNotch size={16} className="animate-spin" /> Verifying reset link…
          </div>
        )}

        {tokenValid === false && (
          <div className="py-6 text-center">
            <XCircle size={28} weight="duotone" className="text-red-500 mx-auto" />
            <div className="text-sm text-[#0F1E38] font-medium mt-3">Link invalid or expired</div>
            <div className="text-xs text-slate-500 mt-1">Request a new reset link from the sign-in page.</div>
            <Button data-testid="back-to-login" onClick={() => nav("/login")} className="mt-6 w-full h-11 bg-[#0F1E38] hover:bg-[#1E3A5F] text-white">Back to sign in</Button>
          </div>
        )}

        {tokenValid && !done && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">New Password</Label>
              <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" minLength={8} required autoFocus/>
              <div className="text-[10.5px] text-slate-500 mt-1.5 font-mono">Minimum 8 characters</div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Confirm Password</Label>
              <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-11" minLength={8} required/>
            </div>
            {error && <div data-testid="reset-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
            <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full h-11 bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Update Password"}
            </Button>
          </form>
        )}

        {done && (
          <div className="py-6 text-center">
            <CheckCircle size={28} weight="duotone" className="text-emerald-500 mx-auto" />
            <div className="text-sm text-[#0F1E38] font-medium mt-3">Password updated</div>
            <div className="text-xs text-slate-500 mt-1">Redirecting to sign in…</div>
          </div>
        )}
      </div>
    </div>
  );
}
